import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  useToast,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaUserAlt, FaIdBadge, FaBuilding, FaUserEdit } from 'react-icons/fa';
import { PhoneIcon, EditIcon } from '@chakra-ui/icons';
import Select from 'react-select';
import axios from 'axios';
import baseUrl from '../../../Base_Url/baseUrl';

const EditUserModal = ({
  isOpen,
  onClose,
  editUser,
  setEditUser,
  roleOptions,
  renderRegionSelectors,
  token,
  getUsers,
  addmodalheaderBg = 'blue.600',
}) => {
  const initialLoadRef = useRef(true);
  const toast = useToast();
  const [divisions, setDivisions] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [tahsils, setTahsils] = useState(null);
  const [villages, setVillages] = useState([]);
  const prevDivision = useRef();
  const prevDistrict = useRef();
  const prevTahsil = useRef();
  const prevRole = useRef();
  const modalBodyBg = useColorModeValue('white', 'gray.800');
  const modalTextColor = useColorModeValue('blue.800', 'blue.200');
  const footerBg = useColorModeValue('gray.50', 'gray.700');
  const footerBorderColor = useColorModeValue('gray.200', 'gray.600');
  const multiBg = useColorModeValue('#BEE3F8', '#4A5568');

  const selectBg = useColorModeValue('#fff', '#2D3748');
  const selectColor = useColorModeValue('black', 'white');
  const menuBg = useColorModeValue('white', '#1A202C');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: selectBg,
      color: selectColor,
      borderColor: state.isFocused ? '#3182ce' : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : base.boxShadow,
      '&:hover': {
        borderColor: '#3182ce',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: menuBg,
      color: selectColor,
    }),
    singleValue: (base) => ({
      ...base,
      color: selectColor,
    }),
    placeholder: (base) => ({
      ...base,
      color: placeholderColor,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const districtOptions = districts?.map((d) => ({
    value: d._id,
    label: d.name,
  }));

  const fetchInitialDivisions = async () => {
    try {
      const res = await axios.get(`${baseUrl}/master/all-divisions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(res?.data?.data?.data);
      setDivisions(res?.data?.data?.data || []);
    } catch {
      toast({ title: 'Failed to load divisions', status: 'error' });
    }
  };

  useEffect(() => {
    if (
      editUser?.role &&
      prevRole.current &&
      editUser.role !== prevRole.current
    ) {
      setEditUser((prev) => ({
        ...prev,
        division: '',
        district: '',
        tahsil: '',
        village: [],
      }));

      setDistricts([]);
      setTahsils([]);
      setVillages([]);

      fetchInitialDivisions();
    }
    prevRole.current = editUser?.role;
  }, [editUser?.role]);

  const fetchDistrictsByDivision = async (divisionId) => {
    try {
      const res = await axios.get(`${baseUrl}/master/all-districts`, {
        params: { divisionId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDistricts(res?.data?.data?.data || []);
    } catch {
      toast({ title: 'Failed to load district', status: 'error' });
    }
  };

  const fetchTahsilsByDistrict = async (districtId) => {
    try {
      const res = await axios.get(`${baseUrl}/master/all-tahsils`, {
        params: { districtId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTahsils(res?.data?.data?.data || []);
    } catch {
      toast({ title: 'Failed to load tahsil', status: 'error' });
    }
  };

  const fetchVillagesByTahsil = async (tahsilId) => {
    try {
      const res = await axios.get(`${baseUrl}/master/all-villages`, {
        params: { tahsilId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setVillages(Array.isArray(res.data.data.data) ? res.data.data.data : []);
    } catch {
      toast({ title: 'Failed to load villages', status: 'error' });
    }
  };

  const fetchInitialRegions = async () => {
    await fetchInitialDivisions();

    if (editUser?.division) await fetchDistrictsByDivision(editUser.division);
    if (editUser?.district) await fetchTahsilsByDistrict(editUser.district);
    if (editUser?.tahsil) await fetchVillagesByTahsil(editUser.tahsil);

    initialLoadRef.current = false;
  };

  useEffect(() => {
    if (isOpen && editUser?._id) {
      fetchInitialRegions();
      initialLoadRef.current = true;
    }
  }, [isOpen, editUser?._id, token]);

  useEffect(() => {
    if (editUser?.division && !initialLoadRef.current) {
      setEditUser((prev) => ({
        ...prev,
        district: '',
        tahsil: '',
        village: [],
      }));
      setDistricts([]);
      setTahsils([]);
      setVillages([]);
      fetchDistrictsByDivision(editUser.division);
    }
  }, [editUser?.division]);

  useEffect(() => {
    if (editUser?.district && !initialLoadRef.current) {
      setEditUser((prev) => ({
        ...prev,
        tahsil: '',
        village: [],
      }));
      setTahsils([]);
      setVillages([]);
      fetchTahsilsByDistrict(editUser.district);
    }
  }, [editUser?.district]);

  useEffect(() => {
    if (editUser?.tahsil && !initialLoadRef.current) {
      setEditUser((prev) => ({
        ...prev,
        village: [],
      }));
      setVillages([]);
      fetchVillagesByTahsil(editUser.tahsil);
    }
  }, [editUser?.tahsil]);

  const handleUpdate = async () => {
    try {
      const updatePayload = {
        name: editUser?.name,
        phoneNo: editUser?.phoneNo,
        role: editUser?.role,
        gender: editUser?.gender,
        active: editUser?.active,
      };

      const role = editUser?.role;

      if (role === 'talati') {
        updatePayload.village = editUser?.village;
        updatePayload.district = editUser?.district;
        updatePayload.tahsil = editUser?.tahsil;
        updatePayload.division = editUser?.division;
      } else if (role === 'tahsildar') {
        updatePayload.district = editUser?.district;
        updatePayload.tahsil = editUser?.tahsil;
        updatePayload.division = editUser?.division;
      } else if (role === 'collector') {
        updatePayload.division = editUser?.division;
        updatePayload.district = editUser?.district;
      } else if (role === 'division') {
        updatePayload.division = editUser?.division;
      }

      await axios.put(
        `${baseUrl}/auth/updateUser/${editUser._id}`,
        updatePayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast({ title: 'User updated', status: 'success' });
      onClose();
      getUsers();
    } catch (e) {
      toast({
        title: e?.response?.data?.data
          ? e?.response?.data?.data
          : e?.response?.data?.data || 'Update failed',
        status: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent rounded="lg" shadow="lg" overflow="hidden">
        <Box
          bg={addmodalheaderBg}
          py={1}
          px={2}
          textAlign="center"
          roundedTop="lg"
        >
          <ModalHeader
            fontSize="2xl"
            fontWeight="bold"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={EditIcon} mr={2} />
            Edit User
          </ModalHeader>
          <ModalCloseButton color="white" />
        </Box>

        <ModalBody bg={modalBodyBg} px={6} py={4}>
          <Text fontSize="lg" fontWeight="bold" color="blue.500" mb={2}>
            <Icon as={FaUserAlt} mr={2} />
            Basic Details
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={FaUserAlt} mr={1} /> Name
              </FormLabel>
              <Input
                value={editUser?.name || ''}
                onChange={(e) =>
                  setEditUser({ ...editUser, name: e.target.value })
                }
                placeholder="Enter name"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={PhoneIcon} mr={1} /> Phone No
              </FormLabel>
              <Input
                value={editUser?.phoneNo || ''}
                onChange={(e) =>
                  setEditUser({ ...editUser, phoneNo: e.target.value })
                }
                placeholder="Enter phone number"
              />
            </FormControl>
          </SimpleGrid>

          <Text fontSize="lg" fontWeight="bold" color="blue.500" mb={2}>
            <Icon as={FaIdBadge} mr={2} />
            Role & Gender
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            <FormControl isRequired>
              <FormLabel>
                <Icon as={FaIdBadge} mr={1} /> Role
              </FormLabel>
              <Select
                options={roleOptions}
                styles={selectStyles}
                value={roleOptions.find((opt) => opt.value === editUser?.role)}
                onChange={(opt) => {
                  setEditUser({
                    ...editUser,
                    role: opt.value,
                    division: '',
                    tahsil: '',
                    village: [],
                    district: '',
                  });
                  setDivisions([]);
                  setDistricts([]);
                  setTahsils([]);
                  setVillages([]);

                  fetchInitialDivisions();
                }}
                placeholder="Select Role"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </FormControl>
            <FormControl>
              <FormLabel>
                <Icon as={FaUserAlt} mr={1} /> Gender
              </FormLabel>
              <Select
                styles={selectStyles}
                options={[
                  { value: '', label: 'Select Gender' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                ]}
                value={[
                  { value: '', label: 'Select Gender' },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                ].find((opt) => opt.value === editUser?.gender)}
                onChange={(opt) =>
                  setEditUser({ ...editUser, gender: opt.value })
                }
                placeholder="Select Gender"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </FormControl>
          </SimpleGrid>

          <Text fontSize="lg" fontWeight="bold" color="blue.500" mb={2}>
            <Icon as={FaBuilding} mr={2} />
            Region Assignment
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
            {['division', 'collector', 'tahsildar', 'talati'].includes(
              editUser?.role,
            ) && (
              <FormControl isRequired>
                <FormLabel>Division</FormLabel>
                <Select
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                  options={divisions?.map((d) => ({
                    value: d._id,
                    label: d.name,
                  }))}
                  value={divisions
                    ?.map((d) => ({ value: d._id, label: d.name }))
                    .find((opt) => opt.value === editUser?.division)}
                  onChange={(opt) => {
                    const divisionId = opt?.value || '';

                    setEditUser({
                      ...editUser,
                      division: divisionId,
                      district: '',
                      tahsil: '',
                      village: [],
                    });

                    setDistricts([]);
                    setTahsils([]);
                    setVillages([]);

                    if (divisionId) fetchDistrictsByDivision(divisionId);
                  }}
                />
              </FormControl>
            )}

            {['collector', 'tahsildar', 'talati'].includes(editUser?.role) && (
              <FormControl isRequired>
                <FormLabel>District</FormLabel>
                <Select
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                  isDisabled={editUser?.division === ''}
                  options={districts?.map((d) => ({
                    value: d._id,
                    label: d.name,
                  }))}
                  value={districts
                    ?.map((d) => ({ value: d._id, label: d.name }))
                    .find((opt) => opt.value === editUser?.district)}
                  onChange={(opt) => {
                    const districtId = opt?.value || '';

                    setEditUser({
                      ...editUser,
                      district: districtId,
                      tahsil: '',
                      village: [],
                    });

                    setTahsils([]);
                    setVillages([]);

                    if (districtId) fetchTahsilsByDistrict(districtId);
                  }}
                />
              </FormControl>
            )}

            {['tahsildar', 'talati'].includes(editUser?.role) && (
              <FormControl isRequired>
                <FormLabel>Tahsil</FormLabel>
                <Select
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                  isDisabled={editUser?.district === ''}
                  options={tahsils?.map((t) => ({
                    value: t._id,
                    label: t.name,
                  }))}
                  value={tahsils
                    ?.map((t) => ({ value: t._id, label: t.name }))
                    .find((opt) => opt.value === editUser?.tahsil)}
                  onChange={(opt) => {
                    const tahsilId = opt?.value || '';

                    setEditUser({
                      ...editUser,
                      tahsil: tahsilId,
                      village: [],
                    });

                    setVillages([]);

                    if (tahsilId) fetchVillagesByTahsil(tahsilId);
                  }}
                />
              </FormControl>
            )}

            {editUser?.role === 'talati' && (
              <FormControl isRequired>
                <FormLabel>Village</FormLabel>
                <Select
                  isMulti
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                  isDisabled={editUser?.tahsil === ''}
                  options={villages?.map((v) => ({
                    value: v._id,
                    label: v.name,
                  }))}
                  value={villages
                    ?.map((v) => ({ value: v._id, label: v.name }))
                    .filter((opt) => editUser?.village?.includes(opt.value))}
                  onChange={(opts) =>
                    setEditUser({
                      ...editUser,
                      village: opts.map((opt) => opt.value),
                    })
                  }
                />
              </FormControl>
            )}
          </SimpleGrid>

          <Text fontSize="lg" fontWeight="bold" color="blue.500" mb={2}>
            <Icon as={FaIdBadge} mr={2} />
            Status
          </Text>
          <FormControl display="flex" alignItems="center" mb={4}>
            <FormLabel htmlFor="active-switch" mb="0" display={'flex'}>
              <Icon as={FaIdBadge} mr={1} />
              <Text ml={2}>{editUser?.active ? 'Active' : 'Inactive'}</Text>
            </FormLabel>
            <Switch
              id="active-switch"
              colorScheme="green"
              isChecked={!!editUser?.active}
              onChange={(e) => {
                setEditUser({ ...editUser, active: e.target.checked });
              }}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter
          bg={footerBg}
          borderTop="1px solid"
          borderColor={footerBorderColor}
        >
          <Button onClick={onClose} mr={3} variant="ghost" colorScheme="red">
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleUpdate}>
            <Icon as={FaUserEdit} mr={2} /> Update User
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditUserModal;
